
import { mapGetters } from 'vuex'
import PlanNotesPlanPicker from '~/components/plan-notes/PlanNotesPlanPicker.vue'
import { baseCarrierName } from '~/util/plan-name'
import {
    PLAN_TYPE_OPTIONS,
    emptyNoteForm,
    noteFormError,
    noteFormPayload,
    noteToForm,
    planAka,
    planDisplayName,
} from '~/util/plan-notes'

// One plan note's fields: for the quotes page's PlanNoteModal (its plan
// fixed) and the agent Plan Notes page. Emits `save` with a payload carrying
// every scoping field, nulls included, so an edit can clear one.
export default {
    components: { PlanNotesPlanPicker },
    props: {
        // The note being edited; null for a new one.
        note: { type: Object, default: null },
        // A fixed plan for "This plan" (the quotes page). Without one, "This
        // plan" is a carrier -> plan picker over `year`'s catalog.
        plan: { type: Object, default: null },
        // A new note's starting fields: a partial emptyNoteForm().
        defaults: { type: Object, default: () => ({}) },
        year: { type: Number, required: true },
        saving: { type: Boolean, default: false },
    },
    data () {
        const form = this.note ? noteToForm(this.note) : { ...emptyNoteForm(), ...this.defaults }
        if (form.scope === 'plan' && this.plan) { form.plan = this.plan }
        return {
            form,
            tried: false,
            catalogSubtypes: [],
            pickerCarrier: form.plan?.carrier ? baseCarrierName(form.plan.carrier) : null,
        }
    },
    computed: {
        ...mapGetters(['usStates']),
        scopeOptions: () => [
            { value: 'plan', text: 'This plan' },
            { value: 'rule', text: 'Rule' },
        ],
        statesModeOptions: () => [
            { value: 'all', text: 'All States' },
            { value: 'include', text: 'Include States' },
            { value: 'exclude', text: 'Exclude States' },
        ],
        planTypeOptions: () => PLAN_TYPE_OPTIONS,
        // The year's catalog subtypes plus any the note already has, so an
        // edit never hides a saved choice the catalog has since dropped.
        subtypeOptions () {
            return [...new Set([...this.catalogSubtypes, ...this.form.plan_subtype])]
                .sort()
                .map(value => ({ value, text: value }))
        },
        allStatesSelected () {
            return this.usStates.length > 0 && this.form.states.length === this.usStates.length
        },
        someStatesSelected () { return this.form.states.length > 0 && !this.allStatesSelected },
        allSubtypesSelected () {
            return this.subtypeOptions.length > 0 && this.form.plan_subtype.length === this.subtypeOptions.length
        },
        someSubtypesSelected () { return this.form.plan_subtype.length > 0 && !this.allSubtypesSelected },
        error () { return this.tried ? noteFormError(this.form) : null },
    },
    watch: {
        'form.scope' (scope) {
            if (scope === 'plan' && this.plan) { this.form.plan = this.plan }
        },
        'form.plan_type' () {
            this.form.plan_subtype = []
            this.loadSubtypes()
        },
        year () { this.loadSubtypes() },
    },
    created () {
        // Only the newest subtype request's answer is kept.
        this.subtypesSeq = 0
    },
    mounted () {
        this.loadSubtypes()
    },
    methods: {
        planName: planDisplayName,
        planAka,
        async loadSubtypes () {
            const seq = ++this.subtypesSeq
            const resp = await this.$try('getPlanNoteSubtypes', {
                year: this.year,
                plan_type: this.form.plan_type || undefined,
            }, { spinner: false, alert: false })
            if (seq !== this.subtypesSeq) { return }
            this.catalogSubtypes = resp?.results || []
        },
        pickPlan ({ carrier, plan }) {
            this.pickerCarrier = carrier ? carrier.name : null
            this.form.plan = plan
        },
        toggleAllStates (checked) {
            this.form.states = checked ? this.usStates.map(s => s.value) : []
        },
        toggleAllSubtypes (checked) {
            this.form.plan_subtype = checked ? this.subtypeOptions.map(o => o.value) : []
        },
        submit () {
            this.tried = true
            if (noteFormError(this.form)) { return }
            this.$emit('save', noteFormPayload(this.form))
        },
    },
}
